import { useCallback, useEffect, useRef, useState } from "react";

import debounce from "lodash.debounce";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { useToast } from "hooks/toast";

import { hideLoading, showLoading } from "store/modules/loading/actions";

import { AdminArea } from "components/Body/AdminArea";
import { Drawer } from "components/Drawer";

import { hearingTypeAttendantRole } from "services/hearingTypeAttendantRole";

import { LogOperation } from "utils/entities/logOperation";

//Toast

//Permissões
import { FORMS_ADMIN } from "utils/entities/permission";

import { CreateEditProcedureCustomerAttendantRole } from "./CreateEdit";

export interface IHearingTypeAttendantRoleItem {
  hearingTypeId: number;
  hearingTypeName: string;
  active: boolean;
  attendantRoleList: Array<IDefaultObject>;
}

export interface IDefaultObject {
  hearingTypeAttendantRoleId?: number;
  id: number;
  name: string;
  active: boolean;
  attendantRoleId: number;
  hearingTypeId: number;
}
export interface IHearingTypeAttendantRoleResponseAPI {
  customerId: number;
  hearingType: Array<IDefaultObject>;
  attendantRole: Array<IDefaultObject>;
  hearingTypeAttendantRole: Array<IHearingTypeAttendantRoleItem>;
}

export const HearingTypeAttendantRole = () => {
  const { t } = useTranslation();
  const [hearingTypeList, setHearingTypeList] = useState<Array<IDefaultObject>>(
    [],
  );
  const [attendantRoleList, setAttendantRoleList] = useState<
    Array<IDefaultObject>
  >([]);

  const [search, setSearch] = useState("");
  const [situation, setSituation] = useState<any>(undefined);
  const [openModal, setOpenModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [element, setElement] = useState<any>();
  const [postOrPutTrue, setPostOrPutTrue] = useState<boolean>(false);
  const [hearingTypeId, setHearingTypeId] = useState<any>();
  const [currentPage, setCurrentPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const { globalConfigurations } = useSelector(
    (state: any) => state.configurations,
  );
  const dispatch = useDispatch();
  const { addToast } = useToast();
  const customerId = LogOperation.getCustomerId();
  const fetchIdRef = useRef(0);

  useEffect(() => {
    const paginationList = Array.isArray(globalConfigurations)
      ? globalConfigurations.find((item: any) => item?.name === "PaginationList")
      : null;
    const paginationValue = paginationList?.value
      ? parseInt(paginationList.value)
      : 10;

    setPageSize(paginationValue);
  }, [globalConfigurations]);

  useEffect(() => {
    if (pageSize > 0) {
      setCurrentPage(0);
      onLoadList(0);
    }
  }, [customerId, situation, pageSize]);

  async function onLoadList(page?: number, searchTerm?: string) {
    const fetchId = ++fetchIdRef.current;
    dispatch(showLoading());
    setLoading(true);
    const response = await hearingTypeAttendantRole.getAll(
      customerId,
      situation,
      (page ?? currentPage) + 1,
      pageSize,
      searchTerm ?? search,
    );

    if (fetchId !== fetchIdRef.current) return;

    if (response) {
      if (response.status) {
        const paginatedData = response.data?.data?.items
          ? response.data.data
          : response.data?.data ?? response.data;
        const items = Array.isArray(paginatedData)
          ? paginatedData
          : paginatedData?.items ?? [];

        setHearingTypeList(items);
        setTotalCount(paginatedData?.totalCount ?? items.length);
      } else {
        addToast({
          type: "warning",
          title: response.message,
        });
      }
    }
    if (fetchId === fetchIdRef.current) {
      dispatch(hideLoading());
      setLoading(false);
    }
    return [];
  }

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setCurrentPage(0);
      onLoadList(0, value);
    }, 400),
    [customerId, situation, pageSize],
  );

  useEffect(() => {
    return () => debouncedSearch.cancel();
  }, [debouncedSearch]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    debouncedSearch(value);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    onLoadList(page);
  };

  const serverPageCount = Math.ceil(totalCount / pageSize);

  function clear() {
    setIsEdit(false);
    setElement(undefined);
    setOpenModal(false);
  }

  async function onClickHearingType(item: IDefaultObject) {
    setIsEdit(true);

    const returnConsult = await transformeHearingTypeAttendantRoleObject(item);

    setElement(returnConsult);
    setOpenModal(true);
  }

  async function transformeHearingTypeAttendantRoleObject(
    HearingType: IDefaultObject,
  ) {
    setHearingTypeId(HearingType?.id);

    const consult =
      await hearingTypeAttendantRole.getAllRelationsProcedureCustomerTypeAttendantRole(
        LogOperation.getCustomerId(),
        HearingType?.id,
      );

    const attendantRoleList = getAttendantRoleByConsult(consult.data.data);

    setAttendantRoleList(attendantRoleList);

    const returnConsult: IHearingTypeAttendantRoleItem = {
      hearingTypeId: HearingType.hearingTypeId,
      hearingTypeName: HearingType?.name,
      active: HearingType.active,
      attendantRoleList: getRelationshipHearingTypeWithAttendantRole(
        consult.data.data,
      ),
    };

    return returnConsult;
  }

  function getAttendantRoleByConsult(consult: Array<any>) {
    const listAttentandRole: Array<IDefaultObject> = [];

    consult.forEach((element) => {
      listAttentandRole.push({
        id: element.attendantRoleId,
        name: element.attendantRoleName,
        active: element.active,
        hearingTypeAttendantRoleId: element?.id,
        attendantRoleId: element.attendantRoleId,
        hearingTypeId: element.hearingTypeId,
      });
    });

    return listAttentandRole;
  }

  function getRelationshipHearingTypeWithAttendantRole(consult: Array<any>) {
    const listHearingTypeAttentandRole: Array<IDefaultObject> = [];
    consult.forEach((element) => {
      if (element.active === "TRUE") {
        listHearingTypeAttentandRole.push({
          id: element.attendantRoleId,
          name: element.attendantRoleName,
          active: element.active,
          hearingTypeAttendantRoleId: element?.id,
          attendantRoleId: element.attendantRoleId,
          hearingTypeId: element.hearingTypeId,
        });
      }
    });

    return listHearingTypeAttentandRole;
  }

  return (
    <>
      <AdminArea
        loading={loading}
        title={t("audiences.hearingTypeLink.title")}
        InputSearchLabel="Buscar por"
        InputSearchOnChange={handleSearchChange}
        list={hearingTypeList}
        onClick={(item: any) => onClickHearingType(item)}
        setItemReturnClick={setElement}
        setOpenModal={setOpenModal}
        setIsEdit={setIsEdit}
        permissionName={FORMS_ADMIN.HEARING_TYPE_ATTENDANT_ROLE}
        onChangeSituation={(value: any) => {
          setCurrentPage(0);
          setSituation(value);
        }}
        showStatus={true}
        displayAtivoFilter={true}
        searchText={search}
        serverSidePagination
        totalCount={totalCount}
        currentPage={currentPage}
        serverPageCount={serverPageCount}
        onPageChange={handlePageChange}
      />
      <Drawer
        open={openModal}
        onClose={() => clear()}
        paddingLeft={30}
        paddingRight={30}
        title={
          isEdit
            ? t("audiences.hearingTypeLink.linkTitle")
            : t("audiences.hearingTypeLink.createLink")
        }
        onApply={() => setPostOrPutTrue(true)}
      >
        <CreateEditProcedureCustomerAttendantRole
          isEdit={isEdit}
          hearingTypeId={hearingTypeId}
          postOrPutTrue={postOrPutTrue}
          setPostOrPutTrue={setPostOrPutTrue}
          closeModal={openModal}
          setCloseModal={setOpenModal}
          hearingTypeList={hearingTypeList}
          attendantRoleList={attendantRoleList}
          hearingTypeAttendantRoleItem={element}
        />
      </Drawer>
    </>
  );
};
